import { Checkbox, Dropzone, Form, Grid, Select, TextField, useExtensions } from '@approvalmax/ui/src/components';
import { animated, useChain, useSpring, useSpringRef, useTrail } from '@react-spring/web';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import { schema } from '../../schema';
import { companySectors, maxDocumentSize } from './StepForm.constants';
import { messages } from './StepForm.messages';

const AnimatedDiv = styled(animated.div as any)``;
const AnimatedGrid = animated(Grid) as any;

export const StepForm = memo(() => {
    const form = useFormContext<z.infer<typeof schema>>();
    const noBusinessWebsite = form.watch('businessInformation.noBusinessWebsite');
    const extensions = useExtensions(['png', 'jpg', 'pdf']);

    // TODO think about moving this logic to a libs/ui/Trails component
    const springRef = useSpringRef();
    const springs = useSpring({
        ref: springRef,
        size: noBusinessWebsite ? 'auto' : 0,
    });

    const trailsRef = useSpringRef();
    const trails = useTrail(2, {
        ref: trailsRef,
        opacity: noBusinessWebsite ? 1 : 0,
        x: noBusinessWebsite ? 0 : 20,
    });

    useChain([trailsRef, springRef], noBusinessWebsite ? [0, 0] : [0, 0.4]);

    return (
        <Form.Part>
            <Select
                autocomplete
                required
                clearable={false}
                name='businessInformation.sector'
                qa='sector'
                label={messages.sectorLabel}
                placeholder={messages.sectorPlaceholder}
                items={companySectors}
            />

            <TextField
                required={!noBusinessWebsite}
                name='businessInformation.businessWebsite'
                qa='businessWebsite'
                label={messages.websiteLabel}
                placeholder={messages.websitePlaceholder}
            />

            <Checkbox name='businessInformation.noBusinessWebsite' data-qa='noBusinessWebsite'>
                {messages.dontHaveWebsiteLabel}
            </Checkbox>

            <AnimatedGrid gap={16} style={{ height: springs.size }}>
                <AnimatedDiv style={trails[0]}>
                    <TextField
                        autoHeight
                        required
                        multiline
                        minHeight={100}
                        maxHeight={200}
                        name='businessInformation.businessActivityDescription'
                        qa='businessActivityDescription'
                        label={messages.aboutLabel}
                        placeholder={messages.aboutPlaceholder}
                    />
                </AnimatedDiv>

                <AnimatedDiv style={trails[1]}>
                    <Dropzone
                        multiple
                        required
                        name='businessInformation.documentSources'
                        qa='documentSources'
                        label={messages.uploadProof}
                        hint={messages.requirements}
                        accept={extensions}
                        maxSize={maxDocumentSize}
                    />
                </AnimatedDiv>
            </AnimatedGrid>
        </Form.Part>
    );
});

StepForm.displayName = 'StepForm';
